import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/auth";
import { I18nProvider } from "@/i18n";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import PublicTrack from "@/pages/PublicTrack";
import Layout from "@/pages/Layout";
import Dashboard from "@/pages/Dashboard";
import Quote from "@/pages/Quote";
import Routes_ from "@/pages/Routes";
import Users from "@/pages/Users";
import Prices from "@/pages/Prices";
import Volumen from "@/pages/Volumen";
import Clientes from "@/pages/Clientes";
import Envios from "@/pages/Envios";
import Historial from "@/pages/Historial";
import Labels from "@/pages/Labels";
import OfficeExits from "@/pages/OfficeExits";
import Masters from "@/pages/Masters";
import Manifests from "@/pages/Manifests";
import ApiPage from "@/pages/ApiPage";
import Countries from "@/pages/Countries";
import Warehouses from "@/pages/Warehouses";
import Voyages from "@/pages/Voyages";
import Inventory from "@/pages/Inventory";
import Trace from "@/pages/Trace";
import Albaranes from "@/pages/Albaranes";
import Locations from "@/pages/Locations";
import Incidents from "@/pages/Incidents";
import Accounting from "@/pages/Accounting";
import InvoiceSettings from "@/pages/InvoiceSettings";
import MyInvoices from "@/pages/MyInvoices";
import Backups from "@/pages/Backups";
import Drivers from "@/pages/Drivers";
import MyRoute from "@/pages/MyRoute";
import Sales from "@/pages/Sales";
import ClientPricing from "@/pages/ClientPricing";
import "@/App.css";

function Guarded({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="h-screen flex items-center justify-center text-slate-500">…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function Public({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="h-screen flex items-center justify-center text-slate-500">…</div>;
  if (user) return <Navigate to="/" replace />;
  return children;
}

// Role sets extracted at module scope so <Guarded roles={...}> receives a stable
// reference on every render — prevents unnecessary re-mounts of the route tree.
const R = {
  labels:      ["admin","operaciones","ventas","sucursal","almacen"],
  officeExits: ["admin","operaciones","sucursal","almacen","ventas"],
  reception:   ["admin","operaciones","almacen"],
  masters:     ["admin","operaciones","almacen","sucursal","ventas"],
  manifests:   ["admin","operaciones","sucursal","almacen"],
  adminOnly:   ["admin"],
  sales:       ["admin","ventas","sucursal"],
  inventory:   ["admin","almacen","operaciones","sucursal"],
  trace:       ["admin","operaciones","almacen","sucursal","ventas","contable"],
  albaranes:   ["admin","operaciones","sucursal","almacen"],
  locations:   ["admin","almacen"],
  incidents:   ["admin","operaciones","almacen","sucursal","ventas"],
  accounting:  ["admin","contable","sucursal"],
  clientOnly:  ["cliente"],
  drivers:     ["admin","operaciones","sucursal"],
  myRoute:     ["conductor","admin","operaciones"],
};

export default function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Public><Login /></Public>} />
            <Route path="/register" element={<Public><Register /></Public>} />
            <Route path="/track" element={<PublicTrack />} />
            <Route path="/track/:code" element={<PublicTrack />} />
            <Route element={<Guarded><Layout /></Guarded>}>
              <Route path="/" element={<Dashboard />} />
              <Route path="/cotizar" element={<Quote />} />
              <Route path="/rutas" element={<Routes_ />} />
              <Route path="/usuarios" element={<Users />} />
              <Route path="/precios" element={<Prices />} />
              <Route path="/volumen" element={<Volumen />} />
              <Route path="/precios-especiales" element={<Guarded roles={R.adminOnly}><ClientPricing /></Guarded>} />
              <Route path="/clientes" element={<Clientes />} />
              <Route path="/envios" element={<Envios />} />
              <Route path="/historial" element={<Historial />} />
              <Route path="/etiquetas" element={<Guarded roles={R.labels}><Labels /></Guarded>} />
              <Route path="/salidas-oficina" element={<Guarded roles={R.officeExits}><OfficeExits /></Guarded>} />
              <Route path="/recepcion" element={<Guarded roles={R.reception}><OfficeExits mode="reception" /></Guarded>} />
              <Route path="/masters" element={<Guarded roles={R.masters}><Masters /></Guarded>} />
              <Route path="/ventas" element={<Guarded roles={R.sales}><Sales /></Guarded>} />
              <Route path="/manifiestos" element={<Guarded roles={R.manifests}><Manifests /></Guarded>} />
              <Route path="/paises" element={<Guarded roles={R.adminOnly}><Countries /></Guarded>} />
              <Route path="/almacenes" element={<Guarded roles={R.adminOnly}><Warehouses /></Guarded>} />
              <Route path="/inventario" element={<Guarded roles={R.inventory}><Inventory /></Guarded>} />
              <Route path="/trazabilidad" element={<Guarded roles={R.trace}><Trace /></Guarded>} />
              <Route path="/programacion" element={<Guarded><Voyages /></Guarded>} />
              <Route path="/albaranes" element={<Guarded roles={R.albaranes}><Albaranes /></Guarded>} />
              <Route path="/ubicaciones" element={<Guarded roles={R.locations}><Locations /></Guarded>} />
              <Route path="/incidencias" element={<Guarded roles={R.incidents}><Incidents /></Guarded>} />
              <Route path="/contabilidad" element={<Guarded roles={R.accounting}><Accounting /></Guarded>} />
              <Route path="/ajustes-facturacion" element={<Guarded roles={R.adminOnly}><InvoiceSettings /></Guarded>} />
              <Route path="/mis-facturas" element={<Guarded roles={R.clientOnly}><MyInvoices /></Guarded>} />
              <Route path="/conductores" element={<Guarded roles={R.drivers}><Drivers /></Guarded>} />
              <Route path="/mi-ruta" element={<Guarded roles={R.myRoute}><MyRoute /></Guarded>} />
              <Route path="/backups" element={<Guarded roles={R.adminOnly}><Backups /></Guarded>} />
              <Route path="/api-docs" element={<Guarded roles={R.adminOnly}><ApiPage /></Guarded>} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="bottom-end" richColors />
      </AuthProvider>
    </I18nProvider>
  );
}
